import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Footer from '../Footer';
import { getDocumentUrl } from '../../utils/documentUrl';
import { filterDocumentsForPublicMenu } from '../../utils/documentDev';
import './styles/DocumentsPage.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const DocumentsPage = () => {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const [allDocuments, setAllDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDocuments = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [navRes, footerRes] = await Promise.all([
          fetch(`${API_BASE}/documents?placement=nav`),
          fetch(`${API_BASE}/documents?placement=footer`)
        ]);

        if (!navRes.ok || !footerRes.ok) {
          throw new Error('Nem sikerült betölteni a dokumentumokat.');
        }

        const [navDocsRaw, footerDocsRaw] = await Promise.all([navRes.json(), footerRes.json()]);
        const navDocs = filterDocumentsForPublicMenu(Array.isArray(navDocsRaw) ? navDocsRaw : []);
        const footerDocs = filterDocumentsForPublicMenu(
          Array.isArray(footerDocsRaw) ? footerDocsRaw : [],
          { allowExternalUrl: true }
        );

        // Only admin-uploaded docs: requires DB id + stored file path, excludes default external links.
        const uploaded = [...navDocs, ...footerDocs].filter(
          (doc) => doc?._id && doc?.filePath && !doc?.externalUrl
        );

        const deduped = Array.from(new Map(uploaded.map((doc) => [doc._id, doc])).values());
        setAllDocuments(deduped);
      } catch (err) {
        setError(err.message || 'Hiba történt a dokumentumok betöltésekor.');
        setAllDocuments([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadDocuments();
  }, []);

  const selectedDocument = useMemo(
    () => allDocuments.find((doc) => doc._id === documentId) || allDocuments[0] || null,
    [allDocuments, documentId]
  );

  const canonicalPath = useMemo(() => {
    const documentSlug = selectedDocument?._id;
    if (!documentSlug) return '/dokumentumok';
    return `/dokumentumok/${documentSlug}`;
  }, [selectedDocument]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [canonicalPath, navigate]);

  const handleDocumentSelect = (nextDocumentId) => {
    navigate(`/dokumentumok/${nextDocumentId}`);
  };

  const pdfUrl = selectedDocument ? getDocumentUrl(selectedDocument) : null;

  return (
    <div className="documents-page">
      <div className="documents-container">
        <div className="documents-header">
          <h1>Dokumentumok</h1>
          <p>Fontos dokumentumok és információk a Napvirág Idősek Otthonáról</p>
        </div>

        <div className="documents-content">
          {/* Documents Sidebar */}
          <div className="documents-sidebar">
            <h3>Dokumentumok</h3>
            <div className="categories-list">
              {allDocuments.map((document) => (
                <button
                  key={document._id}
                  className={`category-item ${selectedDocument?._id === document._id ? 'active' : ''}`}
                  onClick={() => handleDocumentSelect(document._id)}
                >
                  <span className="category-icon">📄</span>
                  <div className="category-info">
                    <h4>{document.name}</h4>
                    <p>{document.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Documents Display */}
          <div className="documents-main">
            {isLoading && (
              <div className="documents-empty-state">
                <p>Dokumentumok betöltése…</p>
              </div>
            )}

            {!isLoading && error && (
              <div className="documents-empty-state">
                <p>{error}</p>
              </div>
            )}

            {!isLoading && !error && selectedDocument && (
              <>
                <div className="category-header">
                  <div className="category-title">
                    <span className="category-icon-large">📄</span>
                    <div>
                      <h2>{selectedDocument.name}</h2>
                      <p>{selectedDocument.description}</p>
                    </div>
                  </div>
                </div>

                {selectedDocument ? (
                  <div className="documents-viewer">
                    {pdfUrl ? (
                      <>
                        <iframe
                          src={`${pdfUrl}#page=1`}
                          title={selectedDocument.name}
                          className="documents-pdf-frame"
                        />
                        <div className="documents-actions">
                          <a
                            href={pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="documents-action-btn documents-action-btn--download"
                          >
                            Letöltés PDF-ben
                          </a>
                          <a
                            href={pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="documents-action-btn documents-action-btn--view"
                          >
                            Teljes képernyős nézet
                          </a>
                        </div>
                      </>
                    ) : (
                      <div className="documents-empty-state">
                        <p>Ehhez a dokumentumhoz még nincs PDF csatolva.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="documents-empty-state">
                    <p>Nincs megjeleníthető dokumentum.</p>
                  </div>
                )}
              </>
            )}

            {!isLoading && !error && allDocuments.length === 0 && (
              <div className="documents-empty-state">
                <p>Még nincs admin felületen feltöltött dokumentum.</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="documents-info">
          <div className="info-card">
            <h3>📞 Segítségre van szüksége?</h3>
            <p>Ha bármilyen kérdése van a dokumentumokkal kapcsolatban, forduljon hozzánk bizalommal.</p>
            <div className="contact-info">
              <p><strong>Telefon:</strong> +36 26 541 000</p>
              <p><strong>Email:</strong> napviragotthon@napviragotthon.hu</p>
            </div>
          </div>
          
          <div className="info-card">
            <h3>ℹ️ Fontos információk</h3>
            <ul>
              <li>Az összes dokumentum PDF formátumban érhető el</li>
              <li>A dokumentumok megtekintéséhez PDF olvasó szükséges</li>
              <li>A dokumentumok rendszeresen frissítésre kerülnek</li>
              <li>Kérdés esetén személyesen is ellátogathat hozzánk</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default DocumentsPage;
